import React from 'react';
import { useRouter } from 'next/router';

import { localeToLang } from 'providers/prismic/utils/localeToLang';
import { getStoryBySlug, IPrismicStory } from 'providers/prismic/stories';
import { getLayout, IPrismicLayout } from 'providers/prismic/layout';
import { Layout } from 'containers/layout/Layout';
import { Slices } from 'containers/slices/Slices';
import { Error } from 'components/error/Error';
import { Header } from 'components/header/Header';
import { ArticleHero } from 'components/hero/ArticleHero';
import { createSlug } from 'providers/prismic/utils/createSlug';
import { config } from 'utils/config';

type Props = {
  preview: boolean;
  layout: IPrismicLayout;
  story: IPrismicStory;
  url: string;
};

export default function StoryPage({ preview, layout, story, url }: Props) {
  const router = useRouter();

  if (router.isFallback) {
    return <Loading />;
  }

  if (!story) {
    return <Error title="Whoops!" statusCode={404} />;
  }

  const extract = story.extract && `${story.extract?.slice(0, 147)}...`;

  return (
    <Layout
      layout={layout}
      seo={{
        title: story.seo?.title || story.title,
        description: story.seo?.description || extract,
        image: story.seo?.image || story.image,
        canonicalUrl: url,
      }}
      header={<Header />}
      hasFooter
      isPadded
      preview={preview}
    >
      <ArticleHero
        title={story.title}
        image={story.image}
        caption={story.caption}
        tagline={story.location}
        // back="/stories"
        // backLabel="Back to Stories"
      />

      <Slices slices={story.slices} isArticle />
    </Layout>
  );
}

const Loading = () => <p>Loading...</p>;

export async function getStaticProps({ params, preview = false, previewData, locale }) {
  const lang = localeToLang(locale);

  const layout = await getLayout({ previewData, lang });
  const story = await getStoryBySlug(params.story, {
    previewData,
    lang,
    redirects: layout?.redirects,
  });

  return {
    props: {
      preview,
      layout,
      story,
      url: createSlug({
        includeSiteUrl: true,
        lang: lang,
        prefix: 'stories',
        uid: story?.uid,
        layouts: [layout],
      }),
    },
    revalidate: config.revalidate,
  };
}

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking',
  };
}
